<template>
  <clinician-app-layout>
    <template #header>
      <inertia-link
        :href="route('clinician.shared-with-me')"
        class="lg:absolute relative md:left-7 flex items-center text-vice-teal-500 text-xs"
      >
        <ChevronLeftIcon class="w-4 h-4 mr-1" />
        <span class="flex-none">Shared With Me</span>
      </inertia-link>
      <div class="py-5 2xl:pt-0">
        <h2 class="text-3xl max-w-3xl text-vice-teal-900 font-semibold leading-tight">
          {{ recording.session_name }}
        </h2>
        <p class="mt-1">
          <span class="text-sm text-gray-600 mt-2 bg-gray-150 p-2 rounded-md">
            Shared by
            <span class="font-medium text-gray-700">{{ recording.clinician.name }}</span>
          </span>
          <span class="ml-2 text-sm text-gray-600 mt-2 bg-gray-150 p-2 rounded-md">
            {{ recording.shared_at }}
          </span>
        </p>
        <p class="text-sm text-gray-600 mt-2">
          {{ recording.device_type.replace(/_/g, ' ') }} - {{ recording.duration }}
        </p>
      </div>
    </template>

    <div class="max-w-10xl mx-auto pt-6 pb-10 px-2 sm:px-6">
      <div class="grid grid-cols-12 gap-6">
        <!-- Preview and Annotations Column -->
        <div class="col-span-12 lg:col-span-8">
          <!-- Preview Section -->
          <div class="bg-white border shadow sm:rounded-lg">
            <div class="p-4 border-b bg-light-grey sm:rounded-t-lg">
              <h3 class="text-lg leading-7 font-medium text-vice-teal-700">Recording Preview</h3>
            </div>
            <div class="p-4 bg-white sm:rounded-lg">
              <div class="relative video-container">
                <video
                  ref="videoPlayer"
                  :key="recording.id"
                  :controls="previewLoaded"
                  controlsList="nodownload"
                  @mouseenter="isMouseOver = true"
                  @mouseleave="isMouseOver = false"
                  @loadeddata="handleVideoLoaded"
                  @play="isPlaying = true"
                  @pause="isPlaying = false"
                  @ended="isPlaying = false"
                  :class="[
                    isAudio(recording.device_type)
                      ? 'h-12 bg-player-grey rounded-full'
                      : 'h-auto bg-black rounded-lg',
                    { 'opacity-0': !previewLoaded },
                  ]"
                  class="
                    ring-1 ring-vice-teal-700
                    overflow-hidden
                    object-contain
                    shadow-lg
                    relative
                    zoom-111
                    w-full
                    block
                  "
                  :src="recording.file_url"
                >
                  Your browser does not support the video tag.
                </video>
                <ViceLoader
                  v-if="!previewLoaded"
                  class="absolute inset-0 flex items-center justify-center z-20"
                  colorClass="text-vice-teal-700"
                  size="h-9 w-9"
                />

                <!-- Comment Markers on Progress Bar -->
                <Transition>
                  <div
                    v-if="previewLoaded && videoDuration > 0 && showPins"
                    :class="[
                      isAudio(recording.device_type)
                        ? 'absolute bottom-11 left-9 right-0 h-1 pointer-events-none mx-16'
                        : 'absolute bottom-9 left-0 right-0 h-1 pointer-events-none mx-5',
                      { 'opacity-0': !previewLoaded },
                    ]"
                  >
                    <div
                      v-for="comment in commentsWithTimestamps"
                      :key="`marker-${comment.id}`"
                      :style="{ left: `${commentShift(comment.timestamp)}%` }"
                      @click="scrollToComment(comment.id)"
                      class="
                        absolute
                        top-6
                        transform
                        -translate-x-1/2
                        pointer-events-auto
                        cursor-pointer
                        group
                      "
                    >
                      <div
                        class="
                          w-6
                          h-6
                          border-2 border-white
                          shadow-lg
                          text-white text-xxs
                          font-semibold
                          flex
                          items-center
                          justify-center
                          transform
                          rotate-135
                          hover:scale-125
                          transition-transform
                          duration-200
                          raindrop
                        "
                        :class="
                          comment.commenter.role === 'clinician'
                            ? 'bg-vice-medium-blue'
                            : 'bg-vice-teal-500'
                        "
                      >
                        <span class="rotate-225 block">
                          {{ getInitials(comment.commenter.name) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </Transition>
              </div>

              <div class="mt-4 text-sm flex">
                <span class="content-center text-gray-600 bg-gray-150 py-1 px-2 rounded-md">
                  File size: {{ recording.file_size }}
                </span>
              </div>
            </div>
          </div>

          <!-- Annotations Section -->
          <div class="shadow mt-6 border sm:rounded-lg">
            <div class="p-4 border-b bg-light-grey sm:rounded-t-lg">
              <h3 class="text-lg leading-7 font-medium text-vice-teal-700">Description</h3>
            </div>
            <div class="px-3 py-3 bg-white rounded-b-lg">
              <div
                v-if="recording.annotations"
                class="prose max-w-none text-sm"
                v-html="recording.annotations"
              ></div>
              <div v-else class="text-gray-500 text-sm italic">No description provided</div>
            </div>
          </div>
        </div>

        <!-- Comments Column -->
        <div class="col-span-12 lg:col-span-4">
          <div class="bg-white border shadow sm:rounded-lg sticky top-6">
            <div class="p-4 border-b bg-light-grey sm:rounded-t-lg">
              <h3 class="text-lg leading-7 font-medium text-vice-teal-700">Comments</h3>
            </div>
            <div class="bg-white sm:rounded-b-lg">
              <!-- Comments List -->
              <div class="px-3 py-3 space-y-4">
                <div
                  v-for="comment in comments"
                  :key="comment.id"
                  :ref="`comment-${comment.id}`"
                  :class="[
                    'border rounded-md border-gray-200 p-3 transition-all duration-500',
                    highlightedCommentId === comment.id ? 'bg-vice-teal-50 ring-2' : '',
                  ]"
                >
                  <div class="flex justify-between pb-1">
                    <div class="flex items-center">
                      <div class="flex items-center">
                        <img
                          v-if="comment.commenter.profile_photo_url"
                          :src="comment.commenter.profile_photo_url"
                          :alt="comment.commenter.name"
                          class="h-8 w-8 rounded-full object-cover"
                        />
                        <div
                          v-else
                          class="
                            h-8
                            w-8
                            rounded-full
                            flex
                            items-center
                            justify-center
                            text-white
                            font-medium
                          "
                          :class="
                            comment.commenter.role === 'clinician'
                              ? 'bg-vice-medium-blue'
                              : 'bg-vice-teal-500'
                          "
                        >
                          {{ getInitials(comment.commenter.name) }}
                        </div>
                      </div>
                      <div class="flex ml-2 gap-2">
                        <p class="flex text-sm font-medium text-gray-900">
                          {{ comment.commenter.name }}
                        </p>
                        <span
                          v-if="comment.commenter.id === recording.clinician.id"
                          class="text-xxs content-center text-gray-600 bg-gray-150 px-1 rounded-md"
                        >
                          Author
                        </span>
                        <span
                          v-else-if="comment.commenter.role === 'clinician'"
                          class="
                            text-xxs
                            content-center
                            bg-vice-light-blue
                            text-gray-600
                            px-1
                            rounded-md
                          "
                        >
                          Clinician
                        </span>
                        <span
                          v-else
                          class="
                            text-xxs
                            content-center
                            bg-vice-teal-100
                            text-gray-600
                            px-1
                            rounded-md
                          "
                        >
                          Student
                        </span>
                      </div>
                    </div>

                    <div class="flex gap-2 items-center">
                      <span class="flex text-xxs text-gray-400">
                        {{ formatDate(comment.created_at) }}
                      </span>
                      <button
                        v-if="canDeleteComment(comment)"
                        @click="deleteComment(comment.id)"
                        class="
                          text-red-500
                          hover:text-red-700
                          transition-colors
                          duration-200
                          p-1
                          rounded
                          hover:bg-red-50
                        "
                        title="Delete comment"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div class="flex-1 mt-1 min-w-0">
                      <span
                        v-if="comment.timestamp !== null && comment.timestamp !== undefined"
                        @click="seekToTimestamp(comment.timestamp)"
                        class="
                          px-2
                          py-1
                          bg-vice-teal-600
                          hover:bg-vice-teal-700
                          rounded-full
                          text-xs text-vice-teal-100
                          cursor-pointer
                          font-bold
                          hover:bg-vice-teal-150
                          justify-evenly
                          w-20
                          flex
                          leading-5
                        "
                      >
                        <span class="px-1 text-xs font-semibold text-gray-100">▶</span>
                        {{ formatTimestamp(comment.timestamp) }}
                      </span>
                      <p
                        class="
                          mt-1
                          ml-1
                          text-sm text-gray-700
                          hover:text-gray-900
                          whitespace-pre-wrap
                        "
                      >
                        {{ comment.comment }}
                      </p>
                    </div>
                  </div>
                </div>
                <div v-if="comments.length === 0" class="text-gray-500 text-sm text-center py-2">
                  No comments yet.
                </div>
              </div>

              <!-- Add Comment Form -->
              <div class="border-t border-gray-200 p-3">
                <form @submit.prevent="submitComment">
                  <textarea
                    v-model="newComment"
                    rows="3"
                    class="
                      block
                      w-full
                      rounded-md
                      border-gray-300
                      shadow-sm
                      focus:border-vice-teal-500
                      focus:ring-vice-teal-500
                      text-sm
                    "
                    placeholder="Add a comment..."
                    :disabled="isSubmitting"
                  ></textarea>
                  <div class="mt-3 flex justify-end">
                    <button
                      type="submit"
                      :disabled="!newComment.trim() || isSubmitting"
                      class="
                        inline-flex
                        items-center
                        px-4
                        py-2
                        border border-transparent
                        rounded-md
                        shadow-sm
                        text-sm
                        font-medium
                        text-white
                        bg-vice-teal-500
                        hover:bg-vice-teal-600
                        focus:outline-none
                        focus:ring-2 focus:ring-offset-2 focus:ring-vice-teal-500
                        disabled:opacity-50
                        disabled:cursor-not-allowed
                      "
                    >
                      <ViceLoader v-if="isSubmitting" class="mr-2 h-4 w-4" />
                      <span>{{ isSubmitting ? 'Posting...' : 'Post Comment' }}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </clinician-app-layout>
</template>

<script>
import { ref, computed } from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ChevronLeftIcon } from '@heroicons/vue/solid';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import ClinicianAppLayout from '@/Layouts/ClinicianAppLayout.vue';

dayjs.extend(relativeTime);

export default {
  name: 'ViewSharedRecording',
  components: {
    ClinicianAppLayout,
    ViceLoader,
    ChevronLeftIcon,
  },
  props: {
    recording: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const isPlaying = ref(false);
    const isMouseOver = ref(false);
    const videoPlayer = ref(null);
    const previewLoaded = ref(false);
    const videoDuration = ref(0);
    const newComment = ref('');
    const isSubmitting = ref(false);
    const comments = ref(props.recording.comments || []);
    const highlightedCommentId = ref(null);

    const commentsWithTimestamps = computed(() =>
      comments.value.filter(
        (comment) => comment.timestamp !== null && comment.timestamp !== undefined,
      ),
    );

    return {
      isPlaying,
      isMouseOver,
      videoPlayer,
      previewLoaded,
      videoDuration,
      newComment,
      isSubmitting,
      comments,
      highlightedCommentId,
      commentsWithTimestamps,
    };
  },
  computed: {
    showPins() {
      return !this.isPlaying || this.isMouseOver;
    },
  },
  methods: {
    isAudio(type) {
      return type === 'Stethoscope';
    },
    getInitials(name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    formatDate(date) {
      return dayjs(date).fromNow();
    },
    formatTimestamp(seconds) {
      const minutes = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    },
    handleVideoLoaded() {
      this.previewLoaded = true;
      if (this.videoPlayer) {
        this.videoDuration = this.videoPlayer.duration;
      }
    },
    seekToTimestamp(timestamp) {
      if (this.videoPlayer && timestamp !== null && timestamp !== undefined) {
        this.videoPlayer.currentTime = parseFloat(timestamp);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.videoPlayer.play();
      }
    },
    commentShift(timestamp) {
      return Math.round((timestamp / (this.recording.duration_raw / 1000)) * 100);
    },
    scrollToComment(commentId) {
      const commentElement = this.$refs[`comment-${commentId}`];
      if (commentElement) {
        // Get the first element if it's an array (Vue can return arrays for refs in v-for)
        const element = Array.isArray(commentElement) ? commentElement[0] : commentElement;

        // Scroll to the comment
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Highlight the comment
        this.highlightedCommentId = commentId;

        // Remove highlight after 2 seconds
        setTimeout(() => {
          this.highlightedCommentId = null;
        }, 2000);
      }
    },
    async submitComment() {
      if (!this.newComment.trim()) return;
      this.isSubmitting = true;

      // Capture current video playback time
      const currentTime = this.videoPlayer?.currentTime || null;

      try {
        const commentData = {
          comment: this.newComment,
          timestamp: currentTime,
        };

        const response = await axios.post(
          route('clinician.shared-content.comments.store', {
            type: 'recording',
            id: this.recording.id,
          }),
          commentData,
        );

        if (response.data.success) {
          this.comments.push(response.data.data);
          this.newComment = '';
          this.added = true;
        } else {
          console.error('Failed to add comment:', response.data.message);
        }
      } catch (error) {
        console.error('Error adding comment:', error);
      } finally {
        this.added = false;
        this.isSubmitting = false;
      }
    },
    canDeleteComment(comment) {
      return comment.commenter_id === this.$page.props.user.id;
    },
    async deleteComment(commentId) {
      try {
        const response = await axios.delete(
          route('clinician.shared-content.comments.destroy', {
            commentId,
          }),
        );

        if (response.data.success) {
          const index = this.comments.findIndex((c) => c.id === commentId);
          if (index !== -1) {
            this.comments.splice(index, 1);
          }
        } else {
          console.error('Failed to delete comment:', response.data.message);
        }
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    },
  },
};
</script>

<style scoped>
.prose {
  color: #374151;
}

.prose :deep(p) {
  margin-bottom: 0.5rem;
}

.prose :deep(ul),
.prose :deep(ol) {
  margin-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.prose :deep(li) {
  margin-bottom: 0.25rem;
}

.raindrop {
  border-radius: 50% 50% 50% 0;
}

.bg-player-grey {
  background-color: #1f2937;
}

.v-enter-active {
  transition: opacity 0.5s ease;
}

.v-leave-active {
  transition: opacity 1s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
